import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const withScheme = (v) => (v && !/^https?:\/\//i.test(v) ? `https://${v}` : v || '');

const TYPE_LABEL = { domain: 'Dominio', ip: 'IP', url: 'URL', email: 'Email' };

const hostFromValue = (v) => {
  if (!v) return '';
  return String(v).replace(/^https?:\/\//i, '').split('/')[0].split(':')[0];
};

/**
 * Selector de objetivos con autorizacion de escaneo vigente.
 * Regla de negocio: no se escanea nada que no este autorizado, asi que el combo es la unica
 * via de entrada. Si el objetivo no existe, se autoriza en el momento sin salir de la pantalla.
 *
 * allowPort: habilita puerto + esquema y ofrece los servicios HTTP detectados por el escaner
 * de puertos, para poder apuntar a aplicaciones que no viven en 80/443.
 */
export const TargetSelect = ({
  value,
  onChange,
  label = 'Objetivo autorizado',
  types = null,
  asUrl = false,
  disabled = false,
  helpText = '',
  testid = 'target-select',
  allowPort = false,
}) => {
  const [auths, setAuths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ target_type: 'domain', target_value: '', authorization_statement: '' });
  const [accepted, setAccepted] = useState(false);

  const [host, setHost] = useState(hostFromValue(value));
  const [port, setPort] = useState('');
  const [scheme, setScheme] = useState('https');
  const [schemeTouched, setSchemeTouched] = useState(false);
  const [discovered, setDiscovered] = useState([]);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const emit = (h, p, s) => {
    if (!h) {
      onChange('');
      return;
    }
    if (!asUrl) {
      onChange(h);
      return;
    }
    if (!allowPort) {
      onChange(withScheme(h));
      return;
    }
    onChange(`${s}://${h}${p ? `:${p}` : ''}`);
  };

  const load = async (select) => {
    try {
      setError('');
      const res = await axios.get(`${API}/scan-authorizations`, { headers: auth() });
      const list = (res.data.authorizations || []).filter(
        (a) => a.is_active !== false && (!types || types.includes(a.target_type))
      );
      setAuths(list);
      if (select) {
        setHost(select);
        emit(select, port, scheme);
      }
    } catch (e) {
      setError('No se pudieron cargar los objetivos autorizados');
    } finally {
      setLoading(false);
    }
  };

  const loadDiscovered = async () => {
    try {
      const res = await axios.get(`${API}/discovered-http-services`, { headers: auth() });
      setDiscovered(res.data.services || []);
    } catch (e) {
      setDiscovered([]);
    }
  };

  useEffect(() => {
    load();
    if (allowPort) loadDiscovered();
  }, []);

  const changeHost = (h) => {
    setHost(h);
    emit(h, port, scheme);
  };

  const changePort = (raw) => {
    const p = raw.replace(/[^0-9]/g, '').slice(0, 5);
    setPort(p);
    let s = scheme;
    if (!schemeTouched) {
      s = !p || p === '443' || p === '8443' ? 'https' : 'http';
      setScheme(s);
    }
    emit(host, p, s);
  };

  const changeScheme = (s) => {
    setScheme(s);
    setSchemeTouched(true);
    emit(host, port, s);
  };

  const useDiscovered = (svc) => {
    setHost(svc.host);
    setPort(String(svc.port));
    setScheme(svc.scheme || 'http');
    setSchemeTouched(true);
    emit(svc.host, String(svc.port), svc.scheme || 'http');
  };

  const submitNew = async () => {
    if (!form.target_value.trim()) {
      setError('Indicá el dominio o IP');
      return;
    }
    if (form.authorization_statement.trim().length < 50) {
      setError('La declaración de autorización debe tener al menos 50 caracteres');
      return;
    }
    if (!accepted) {
      setError('Debés confirmar que tenés autorización para escanear este objetivo');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/scan-authorizations`, form, { headers: auth() });
      const created = form.target_value.trim().toLowerCase();
      setForm({ target_type: 'domain', target_value: '', authorization_statement: '' });
      setAccepted(false);
      setAdding(false);
      await load(created);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al autorizar el objetivo');
    } finally {
      setSaving(false);
    }
  };

  const hostDiscovered = discovered.filter((d) => !host || d.host === host);

  return (
    <div data-testid={`${testid}-wrapper`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>

      <div className="flex gap-2">
        <select
          data-testid={testid}
          value={host}
          disabled={disabled || loading}
          onChange={(e) => changeHost(e.target.value)}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
        >
          <option value="">
            {loading ? 'Cargando objetivos...' : auths.length ? 'Seleccioná un objetivo autorizado' : 'No tenés objetivos autorizados'}
          </option>
          {auths.map((a) => (
            <option key={a.id || a.target_value} value={a.target_value}>
              {a.target_value} ({TYPE_LABEL[a.target_type] || a.target_type})
            </option>
          ))}
        </select>

        {allowPort && (
          <>
            <select
              data-testid={`${testid}-scheme`}
              value={scheme}
              disabled={disabled}
              onChange={(e) => changeScheme(e.target.value)}
              className="w-28 px-2 py-3 border border-gray-300 rounded-lg bg-white"
            >
              <option value="https">https</option>
              <option value="http">http</option>
            </select>
            <input
              data-testid={`${testid}-port`}
              type="text"
              inputMode="numeric"
              value={port}
              disabled={disabled}
              onChange={(e) => changePort(e.target.value)}
              placeholder="Puerto"
              className="w-28 px-3 py-3 border border-gray-300 rounded-lg"
            />
          </>
        )}

        <button
          type="button"
          data-testid={`${testid}-add-btn`}
          onClick={() => { setAdding(!adding); setError(''); }}
          disabled={disabled}
          className="px-4 py-3 border border-blue-600 text-blue-700 font-semibold rounded-lg hover:bg-blue-50 whitespace-nowrap"
        >
          + Agregar objetivo
        </button>
      </div>

      {allowPort && host && (
        <p className="mt-1 text-xs text-gray-600" data-testid={`${testid}-preview`}>
          Se va a escanear: <span className="font-mono font-semibold">{`${scheme}://${host}${port ? `:${port}` : ''}`}</span>
          {!port && ' (puerto por defecto del protocolo)'}
        </p>
      )}

      {allowPort && hostDiscovered.length > 0 && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid={`${testid}-discovered`}>
          <p className="text-xs font-semibold text-blue-900 mb-2">
            Servicios web detectados por el escáner de puertos — tocá uno para apuntar ahí:
          </p>
          <div className="flex flex-wrap gap-2">
            {hostDiscovered.map((d) => (
              <button
                key={`${d.host}:${d.port}`}
                type="button"
                onClick={() => useDiscovered(d)}
                disabled={disabled}
                className="text-xs px-3 py-1 rounded-full border border-blue-400 bg-white text-blue-800 hover:bg-blue-100 font-mono"
                data-testid={`${testid}-discovered-${d.port}`}
              >
                {d.host}:{d.port} · {(d.scheme || 'http').toUpperCase()}
                {d.title ? ` · ${String(d.title).slice(0, 28)}` : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {helpText && !adding && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}

      {!loading && !auths.length && !adding && (
        <p className="mt-2 text-sm text-amber-700" data-testid={`${testid}-empty`}>
          Solo se pueden escanear objetivos con autorización vigente. Agregá el dominio o IP para habilitarlo.
        </p>
      )}

      {adding && (
        <div className="mt-3 p-4 border border-blue-200 bg-blue-50 rounded-lg space-y-3" data-testid={`${testid}-add-form`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              data-testid={`${testid}-new-type`}
              value={form.target_type}
              onChange={(e) => setForm({ ...form, target_type: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
            >
              <option value="domain">Dominio</option>
              <option value="ip">IP</option>
            </select>
            <input
              data-testid={`${testid}-new-value`}
              type="text"
              value={form.target_value}
              onChange={(e) => setForm({ ...form, target_value: e.target.value.trim().toLowerCase() })}
              placeholder="empresa.com.ar o 192.168.1.1"
              className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <textarea
            data-testid={`${testid}-new-statement`}
            value={form.authorization_statement}
            onChange={(e) => setForm({ ...form, authorization_statement: e.target.value })}
            rows={3}
            placeholder="Declaro que soy titular del objetivo indicado o cuento con autorización expresa del titular para que se realicen auditorías de seguridad sobre el mismo."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <p className="text-xs text-gray-500">{form.authorization_statement.trim().length}/50 caracteres mínimos</p>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              data-testid={`${testid}-new-accept`}
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1"
            />
            <span>Confirmo que cuento con autorización para escanear este objetivo y acepto los términos del servicio.</span>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={`${testid}-new-save`}
              onClick={submitNew}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {saving ? 'Autorizando...' : 'Autorizar y usar'}
            </button>
            <button
              type="button"
              data-testid={`${testid}-new-cancel`}
              onClick={() => { setAdding(false); setError(''); }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default TargetSelect;
